import { ChoiceTileOption } from '../../components/choice-tile/types'
import { LeaseOfferStatus } from '../../_types/resident'
import {
  PublicRenewalOffer,
  RenewalOfferDeclineReason,
} from '../../_types/renewal-offer'

/** The reasons a renter can give for declining. Optional on the page. */
const DECLINE_REASON_OPTIONS: ChoiceTileOption<RenewalOfferDeclineReason>[] = [
  { value: 'price', title: 'The price' },
  { value: 'moving-elsewhere', title: 'I am moving elsewhere' },
  { value: 'different-terms', title: 'I want different terms' },
  { value: 'other', title: 'Something else' },
]

/**
 * A monthly rent as the renter reads it.
 *
 * @param {number} cents - the amount in cents.
 * @param {string} currency - the offer currency, e.g. "cad".
 * @returns {string} e.g. "$1,850.00".
 */
const formatRent = (cents: number, currency: string): string =>
  new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: currency.toUpperCase(),
    currencyDisplay: 'narrowSymbol',
  }).format(cents / 100)

/**
 * A lease date as the renter reads it. Lease dates are stored as UTC midnight,
 * so they are shown in UTC or a western renter would see the day before.
 *
 * @param {number} timestamp - the lease date.
 * @returns {string} e.g. "October 1, 2026".
 */
const formatLeaseDate = (timestamp: number): string =>
  new Date(timestamp).toLocaleDateString('en-CA', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

/**
 * Who the offer is from, falling back to something readable when the company
 * name is unknown.
 *
 * @param {PublicRenewalOffer} offer - the offer.
 * @returns {string} the sender.
 */
const describeSender = (offer: PublicRenewalOffer): string =>
  offer.companyName === '' ? 'Your property manager' : offer.companyName

/**
 * Where the renter lives, as one line.
 *
 * @param {PublicRenewalOffer} offer - the offer.
 * @returns {string} e.g. "Unit 12, Bow34 Townhomes", or blank.
 */
const describeHome = (offer: PublicRenewalOffer): string => {
  const unit = offer.unitLabel === '' ? '' : `Unit ${offer.unitLabel}`

  return [unit, offer.propertyName].filter(part => part !== '').join(', ')
}

/**
 * The message for an offer that was already answered or withdrawn when the
 * renter opened the link.
 *
 * @param {LeaseOfferStatus} status - the offer's status.
 * @param {string} sender - who sent it.
 * @returns {{ title: string, body: string }} what to show.
 */
const describeClosedOffer = (
  status: LeaseOfferStatus,
  sender: string,
): { title: string; body: string } => {
  if (status === 'accepted') {
    return {
      title: 'You have accepted this offer',
      body: `${sender} has your acceptance and will be in touch to finalize your renewal.`,
    }
  }

  if (status === 'declined') {
    return {
      title: 'You have declined this offer',
      body: `${sender} has your answer. Reach out to them if you change your mind.`,
    }
  }

  return {
    title: 'This offer is no longer available',
    body: `${sender} has replaced or withdrawn it. If they send you a new one, it will come with its own link.`,
  }
}

export {
  DECLINE_REASON_OPTIONS,
  formatRent,
  formatLeaseDate,
  describeSender,
  describeHome,
  describeClosedOffer,
}
